/**
 * Scroll lock hook with reference counting for nested modals.
 *
 * Uses a position-fixed body lock to prevent background scrolling.
 * This approach avoids iOS Safari's scroll-freeze bug that occurs
 * when `overflow: hidden` is set on the body while nested scrollable
 * elements exist inside `position: fixed` modals.
 *
 * The counter pattern ensures:
 * - First modal to open locks the body
 * - Last modal to close restores the body
 * - Nested modals don't interfere with each other
 */

import { useEffect } from 'react';

// Module-level counter to track how many components want scroll locked
let lockCount = 0;

// Saved body styles and scroll position before the first lock
interface ScrollLockState {
  scrollY: number;
  overflow: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
}

let savedState: ScrollLockState | null = null;

/**
 * Lock body scroll. Call this when a modal opens.
 * Only actually locks on the first call (when count goes from 0 to 1).
 *
 * Uses position-fixed technique instead of overflow-hidden to avoid
 * iOS Safari's touch scroll freeze in nested scroll containers.
 */
function lockScroll(): void {
  if (lockCount === 0) {
    const scrollY = window.scrollY;
    savedState = {
      scrollY,
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }
  lockCount++;
}

/**
 * Unlock body scroll. Call this when a modal closes.
 * Only actually unlocks on the last call (when count goes from 1 to 0).
 */
function unlockScroll(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && savedState) {
    const { scrollY } = savedState;
    document.body.style.overflow = savedState.overflow;
    document.body.style.position = savedState.position;
    document.body.style.top = savedState.top;
    document.body.style.left = savedState.left;
    document.body.style.right = savedState.right;
    document.body.style.width = savedState.width;
    window.scrollTo(0, scrollY);
    savedState = null;
  }
}

/**
 * Hook to manage body scroll lock for modals.
 *
 * Uses a reference counter so nested modals work correctly:
 * - Body scroll is locked when first modal opens
 * - Body scroll is unlocked only when ALL modals are closed
 *
 * @param isLocked - Whether this component wants scroll locked
 *
 * @example
 * ```tsx
 * const Modal = ({ isOpen }) => {
 *   useScrollLock(isOpen);
 *   return isOpen ? <div>Modal content</div> : null;
 * };
 * ```
 */
export function useScrollLock(isLocked: boolean): void {
  useEffect(() => {
    if (isLocked) {
      lockScroll();
      return () => {
        unlockScroll();
      };
    }
    return undefined;
  }, [isLocked]);
}

// Export for testing purposes
export const __testing__ = {
  getLockCount: () => lockCount,
  resetLockCount: () => {
    lockCount = 0;
    savedState = null;
  },
};
